/**
 * The console's light or dark, as an external store for useSyncExternalStore — what the shared
 * next-themes wrapper forces on /admin (components/theme-provider.tsx; spec
 * docs/superpowers/specs/2026-09-27-admin-console-themes-design.md, "Light and dark, the console's
 * own").
 *
 * The snapshot is the hf-admin-ui cookie's mode, resolved against the device for System — but only
 * while a console page is open, i.e. while a console layout has put `data-admin-theme` on <html>.
 * The console owns light / dark exactly as long as it owns the look: an /admin URL no console
 * layout renders (a not-found) keeps the customer app's mode, as it keeps its look.
 *
 * It is read again whenever the picker (or GET admin/me) says the choice changed, whenever the
 * console opens or closes (the attribute comes or goes), whenever the device flips between light
 * and dark, and when the tab comes back into view — another tab may have changed the cookie, and
 * no event crosses tabs.
 */
import { ADMIN_UI_CHANGE_EVENT } from './admin-themes'
import { deviceIsDark, resolveAdminMode, SYSTEM_DARK_QUERY } from './admin-ui'
import { readAdminUiCookie } from './admin-ui-cookie'
import { ADMIN_THEME_ATTRIBUTE } from './admin-ui-dom'

/** Is a console page open: has its layout put the console's attribute on <html>? */
export function adminConsoleIsOpen(): boolean {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute(ADMIN_THEME_ATTRIBUTE)
}

/** The admin's light or dark while a console page is open; undefined otherwise. */
export function adminModeSnapshot(): 'light' | 'dark' | undefined {
  if (!adminConsoleIsOpen()) return undefined
  return resolveAdminMode(readAdminUiCookie().mode, deviceIsDark())
}

export function subscribeAdminMode(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const onVisible = () => {
    if (document.visibilityState === 'visible') onChange()
  }
  window.addEventListener(ADMIN_UI_CHANGE_EVENT, onChange)
  window.addEventListener('focus', onChange)
  document.addEventListener('visibilitychange', onVisible)
  const stopDevice = watchDeviceMode(onChange)
  // The console opening or closing: its attribute appearing on, or leaving, <html>.
  const observer = typeof MutationObserver === 'function' ? new MutationObserver(onChange) : null
  observer?.observe(document.documentElement, { attributes: true, attributeFilter: [ADMIN_THEME_ATTRIBUTE] })
  return () => {
    window.removeEventListener(ADMIN_UI_CHANGE_EVENT, onChange)
    window.removeEventListener('focus', onChange)
    document.removeEventListener('visibilitychange', onVisible)
    stopDevice()
    observer?.disconnect()
  }
}

/** Call `onChange` whenever the device switches between light and dark. Returns the unsubscribe. */
export function watchDeviceMode(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(SYSTEM_DARK_QUERY)
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }
  // Safari before 14 only has the old listener API.
  query.addListener?.(onChange)
  return () => query.removeListener?.(onChange)
}
